import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios'; 
import './App.css'; 

import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import PrestationPage from './components/PrestationPage';
import ProformaPage from './components/ProformaPage';
import FacturePage from './components/FacturePage';
import FactureAvoirPage from './components/FactureAvoirPage';
import BonDeLivraisonPage from './components/BonDeLivraison';
import ClientPage from './components/ClientPage';
import EncaissementPage from './components/EncaissementPage'; 
import SettingsPage from './components/SettingsPage';
import LoginPage from './components/LoginPage';
import AdminResetPage from './components/AdminResetPage';
import LivreTresoreriePage from './components/LivreTresoreriePage'; 

function App() {
    const [user, setUser] = useState(null);

    const handleLogout = useCallback(() => {
        setUser(null);
        localStorage.removeItem('facsmart_user');
    }, []);

    useEffect(() => {
        const storedUser = localStorage.getItem('facsmart_user');
        if (storedUser) {
            try {
                const u = JSON.parse(storedUser);
                setUser(u.user || u); 
            } catch (e) { 
                handleLogout(); 
            }
        }
    }, [handleLogout]);

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response && error.response.status === 401) {
                    console.warn("Token expiré. Déconnexion automatique.");
                    handleLogout(); 
                }
                return Promise.reject(error);
            }
        );
        return () => axios.interceptors.response.eject(interceptor);
    }, [handleLogout]);

    useEffect(() => {
        if (!user) return;
        let timeoutId;
        const TEN_MINUTES = 10 * 60 * 1000;
        const resetTimer = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                alert("Votre session a expiré suite à 10 minutes d'inactivité.");
                handleLogout();
            }, TEN_MINUTES);
        };
        const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];
        events.forEach(event => window.addEventListener(event, resetTimer));
        resetTimer();
        return () => {
            clearTimeout(timeoutId);
            events.forEach(event => window.removeEventListener(event, resetTimer));
        };
    }, [user, handleLogout]);

    const handleLogin = (data) => {
        setUser(data.user);
        localStorage.setItem('facsmart_user', JSON.stringify(data));
    };

    const handleUpdateUser = (updatedUser) => {
        setUser(prev => ({ ...prev, ...updatedUser }));
        const currentStorage = JSON.parse(localStorage.getItem('facsmart_user') || '{}');
        localStorage.setItem('facsmart_user', JSON.stringify({ 
            ...currentStorage, 
            user: { ...currentStorage.user, ...updatedUser } 
        }));
    };

    if (!user) return <LoginPage onLogin={handleLogin} />;

    const userEmail = user?.email?.toLowerCase() || user?.username?.toLowerCase() || '';
    
    const isSuperAdmin = user?.role === 'superadmin'; 
    const isAdmin = user?.role === 'admin' || isSuperAdmin; 
    const isDirection = user?.role === 'direction'; 

    const isKbOuattara = userEmail === 'kb.ouattara@arsn.ci' || userEmail === 'kb.ouattara@gmail.com';
    const isRdBle = userEmail === 'rd.ble@arsn.ci'; 

    const canManageFacturation = !isDirection && (isAdmin || isKbOuattara || isRdBle || user?.role === 'facturation');
    const canManageEncaissement = !isDirection && (isAdmin || isKbOuattara || isRdBle || user?.role === 'finance');
    const canViewTresorerie = !isDirection && (isAdmin || isKbOuattara || isRdBle || user?.role === 'finance');

    return (
        <Router>
            <div className="app-root d-flex">
                <Sidebar user={user} onLogout={handleLogout} />

                <main className="flex-grow-1 p-4 position-relative custom-scrollbar" style={{overflowY: 'auto', height: '100vh', background: 'transparent', color: '#333'}}>

                    <div className="d-flex justify-content-end align-items-center mb-4">
                        <div className="px-3 py-1 rounded shadow-sm" style={{ background: '#ffffff', border: '1px solid #dee2e6' }}>
                            <span className="me-2 text-muted small">SYS. IDENT:</span>
                            <span className="fw-bold" style={{ color: '#1A7A38' }}>{user.fullName || user.username}</span>
                            {isDirection && <span className="badge ms-2" style={{ background: '#ffc107', color: '#000' }}>DIRECTION</span>}
                            {isSuperAdmin && <span className="badge ms-2" style={{ background: '#dc3545', color: '#fff' }}>SUPER ADMIN</span>}
                        </div>
                    </div>

                    <Routes>
                        <Route path="/" element={<Dashboard user={user} />} />
                        <Route path="/prestations" element={canManageFacturation ? <PrestationPage user={user} /> : <Navigate to="/" />} />
                        <Route path="/clients" element={canManageFacturation ? <ClientPage user={user} /> : <Navigate to="/" />} />
                        <Route path="/proformas" element={canManageFacturation ? <ProformaPage user={user} /> : <Navigate to="/" />} />
                        <Route path="/factures" element={canManageFacturation ? <FacturePage user={user} /> : <Navigate to="/" />} />
                        <Route path="/avoirs" element={canManageFacturation ? <FactureAvoirPage user={user} /> : <Navigate to="/" />} />
                        
                        {/* ✅ LA ROUTE MANQUANTE EST AJOUTÉE ICI */}
                        <Route path="/livraisons" element={canManageFacturation ? <BonDeLivraisonPage user={user} /> : <Navigate to="/" />} />

                        <Route path="/encaissements" element={canManageEncaissement ? <EncaissementPage user={user} /> : <Navigate to="/" />} />
                        <Route path="/tresorerie" element={canViewTresorerie ? <LivreTresoreriePage user={user} /> : <Navigate to="/" />} />
                        <Route path="/settings" element={!isDirection ? <SettingsPage user={user} onUpdateUser={handleUpdateUser} /> : <Navigate to="/" />} />
                        <Route path="/admin-reset" element={isSuperAdmin ? <AdminResetPage currentUser={user} /> : <Navigate to="/" />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                </main>
            </div>
        </Router>
    );
}

export default App;